import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, Coins, Key, List, Code2, LogOut, Plus, Trash2, ExternalLink, Copy, Check, Crown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import {
  getCredits, listApiKeys, createApiKey, deleteApiKey, clearToken, loadToken,
  createTopupInvoice,
} from '../api';

const API_BASE = 'https://dynaparse.com/api/v1';

// ─── Code snippets ──────────────────────────────────────────────────────────

function curlSnippet(key) {
  return `curl -X POST ${API_BASE}/ocr/extract \\
  -H "Authorization: Bearer ${key}" \\
  -F "file=@invoice.pdf" \\
  -F "fields=vendor_name,invoice_number,total_amount,due_date"`;
}

const pythonSnippet = `import requests

resp = requests.post(
    "${API_BASE}/ocr/extract",
    headers={"Authorization": "Bearer YOUR_API_KEY"},
    files={"file": open("document.pdf", "rb")},
    data={"fields": "vendor_name,invoice_number,total_amount,due_date"},
)
print(resp.json())`;

// ─── Small helpers ───────────────────────────────────────────────────────────

function StatCard({ icon: Icon, label, value, sub, color = 'text-primary' }) {
  return (
    <div className='bg-card border border-border rounded-2xl p-6 shadow-card'>
      <div className='flex items-start justify-between'>
        <div>
          <p className='text-sm text-muted-foreground mb-1'>{label}</p>
          <p className={`font-heading font-bold text-3xl ${color}`}>{value}</p>
          {sub && <p className='text-xs text-muted-foreground mt-1'>{sub}</p>}
        </div>
        <div className='w-10 h-10 rounded-xl bg-primary/8 flex items-center justify-center'>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
      </div>
    </div>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={copy} className='p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors'>
      {copied ? <Check className='w-3.5 h-3.5 text-success' /> : <Copy className='w-3.5 h-3.5' />}
    </button>
  );
}

// ─── Upgrade Tab ─────────────────────────────────────────────────────────────

const PACKS = [
  { id: 'starter',  name: 'Starter',  credits: '1,000',  price: '$5',   border: 'border-border',   popular: false },
  { id: 'pro',      name: 'Pro',      credits: '5,000',  price: '$15',  border: 'border-primary',  popular: true  },
  { id: 'business', name: 'Business', credits: '20,000', price: '$40',  border: 'border-border',   popular: false },
];

function UpgradeTab({ balance, token }) {
  const [loading, setLoading] = useState(null);

  const handleBuy = async (packId) => {
    setLoading(packId);
    try {
      const data = await createTopupInvoice(token, packId);
      if (!data?.transaction_id) {
        throw new Error('No payment URL received from gateway. Please try again.');
      }
      window.Paddle.Checkout.open({ transactionId: data.transaction_id });
      setLoading(null);
    } catch (err) {
      toast.error(err.message || 'Failed to create invoice');
      setLoading(null);
    }
  };

  const params = new URLSearchParams(window.location.search);
  const topupStatus = params.get('topup');

  return (
    <div className='space-y-6'>
      <h2 className='font-heading font-semibold text-lg'>Top Up Credits</h2>

      {topupStatus === 'success' && (
        <div className='bg-success/10 border border-success/30 rounded-xl px-4 py-3 flex items-center gap-3 text-sm text-success'>
          <Check className='w-4 h-4 flex-shrink-0' />
          Payment successful! Credits have been added to your account. Refresh to see updated balance.
        </div>
      )}
      {topupStatus === 'failed' && (
        <div className='bg-destructive/10 border border-destructive/30 rounded-xl px-4 py-3 text-sm text-destructive'>
          Payment was not completed. You have not been charged.
        </div>
      )}

      {/* Current balance */}
      <div className='bg-card border border-border rounded-2xl p-5 flex items-center gap-4'>
        <div className='w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center'>
          <Coins className='w-5 h-5 text-primary' />
        </div>
        <div>
          <p className='text-sm font-medium text-foreground'>Current Balance</p>
          <p className='text-xs text-muted-foreground'>
            <strong className='text-foreground text-base'>{balance ?? '...'} credits</strong> remaining
          </p>
        </div>
      </div>

      {/* Pack cards */}
      <div className='grid sm:grid-cols-3 gap-4'>
        {PACKS.map((p) => (
          <div key={p.id} className={`relative bg-card border-2 ${p.border} rounded-2xl p-6 flex flex-col gap-4`}>
            {p.popular && (
              <div className='absolute -top-3 left-1/2 -translate-x-1/2'>
                <span className='bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1'>
                  <Zap className='w-3 h-3' /> Most Popular
                </span>
              </div>
            )}
            <div>
              <p className='font-heading font-bold text-lg text-foreground'>{p.name}</p>
              <p className='text-2xl font-bold text-foreground mt-1'>{p.price}</p>
              <p className='text-xs text-muted-foreground mt-1'>{p.credits} credits · one-time</p>
            </div>
            <Button
              className='w-full mt-auto'
              variant={p.popular ? 'default' : 'outline'}
              disabled={!!loading}
              onClick={() => handleBuy(p.id)}
            >
              {loading === p.id ? 'Redirecting…' : `Buy ${p.name}`}
            </Button>
          </div>
        ))}
      </div>

      {/* Payment info */}
      <div className='bg-muted rounded-xl px-4 py-3 text-sm text-muted-foreground'>
        Payments processed by <strong className='text-foreground'>Paddle</strong> — accepts all major credit/debit cards and PayPal.
        Credits are added automatically once payment is confirmed.
      </div>
    </div>
  );
}

// ─── User Type Badge ─────────────────────────────────────────────────────────

function UserTypeBadge({ userType }) {
  if (userType === 'paid') {
    return (
      <span className='inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-primary/10 text-primary border border-primary/30'>
        <Crown className='w-3 h-3' />
        Paid Plan
      </span>
    );
  }
  return (
    <span className='inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-muted text-muted-foreground border border-border'>
      <Zap className='w-3 h-3' />
      Free Plan
    </span>
  );
}

// ─── Dashboard ───────────────────────────────────────────────────────────────

export default function DashboardPage() {
  const navigate = useNavigate();
  const token = loadToken();

  const [balance,    setBalance]    = useState(null);
  const [userType,   setUserType]   = useState('free');
  const [usage,      setUsage]      = useState([]);
  const [apiKeys,    setApiKeys]    = useState([]);
  const [newKey,     setNewKey]     = useState(null);
  const [tab,        setTab]        = useState('overview');
  const [loading,    setLoading]    = useState(true);
  const [creating,   setCreating]   = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('topup') || params.get('upgrade')) setTab('upgrade');
  }, []);

  useEffect(() => {
    if (!token) navigate('/', { replace: true });
  }, [token, navigate]);

  const fetchData = useCallback(async () => {
    if (!token) return;
    try {
      const [credits, keys] = await Promise.all([
        getCredits(token),
        listApiKeys(token),
      ]);
      setBalance(credits.balance);
      setUserType(credits.user_type || 'free');
      setUsage(credits.recent_usage || []);
      setApiKeys(keys.api_keys || []);
    } catch (err) {
      if (err.message?.includes('401') || err.message?.toLowerCase().includes('invalid')) {
        clearToken();
        navigate('/', { replace: true });
      } else {
        toast.error('Failed to load data: ' + err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreateKey = async () => {
    setCreating(true);
    try {
      const data = await createApiKey(token);
      setNewKey(data.api_key);
      await fetchData();
      toast.success("API key created. Copy it now — it won't be shown again.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteKey = async (keyId) => {
    try {
      await deleteApiKey(token, keyId);
      setApiKeys((prev) => prev.filter((k) => k.keyId !== keyId));
      toast.success('API key revoked.');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleLogout = () => {
    clearToken();
    navigate('/');
  };

  const tabs = [
    { id: 'overview',  label: 'Overview',  icon: Coins  },
    { id: 'keys',      label: 'API Keys',  icon: Key    },
    { id: 'usage',     label: 'Usage',     icon: List   },
    { id: 'examples',  label: 'Examples',  icon: Code2  },
    { id: 'upgrade',   label: 'Upgrade',   icon: Crown  },
  ];

  return (
    <div className='min-h-screen bg-background'>
      <Toaster position='top-right' richColors />

      {/* Navbar */}
      <header className='bg-card border-b border-border sticky top-0 z-40'>
        <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <div className='w-7 h-7 rounded-lg bg-primary flex items-center justify-center'>
              <Zap className='w-3.5 h-3.5 text-primary-foreground' strokeWidth={2.2} />
            </div>
            <span className='font-heading font-bold text-base text-foreground'>
              Dyna<span className='text-primary'>Parse</span>
            </span>
            <span className='ml-3 text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full'>Dashboard</span>
          </div>
          <div className='flex items-center gap-3'>
            <a
              href='https://dynaparse.com/docs'
              target='_blank'
              rel='noopener noreferrer'
              className='text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors'
            >
              API Docs <ExternalLink className='w-3 h-3' />
            </a>
            <Button variant='ghost' size='sm' onClick={handleLogout} className='gap-1.5'>
              <LogOut className='w-4 h-4' /> Sign Out
            </Button>
          </div>
        </div>
      </header>

      <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10'>

        {/* Tab bar */}
        <div className='flex gap-1 bg-muted p-1 rounded-xl w-fit mb-8'>
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
                  tab === t.id
                    ? 'bg-card shadow-card text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon className='w-4 h-4' />
                {t.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className='text-center py-20 text-muted-foreground'>Loading...</div>
        ) : (
          <>
            {/* ── Overview ── */}
            {tab === 'overview' && (
              <div className='space-y-6'>
                <div className='flex items-center gap-3'>
                  <UserTypeBadge userType={userType} />
                  {userType === 'free' && (
                    <span className='text-xs text-muted-foreground'>
                      10 bonus credits on signup.{' '}
                      <button onClick={() => setTab('upgrade')} className='text-primary hover:underline'>
                        Top up to get more →
                      </button>
                    </span>
                  )}
                </div>
                <div className='grid sm:grid-cols-3 gap-4'>
                  <StatCard icon={Coins}  label='Credit Balance'  value={balance ?? '—'} sub='credits remaining' color='text-primary' />
                  <StatCard icon={Key}    label='API Keys'        value={apiKeys.length} sub='active keys' />
                  <StatCard icon={List}   label='Recent Usage'    value={usage.length}   sub='last 20 events' />
                </div>

                <div className='bg-card border border-border rounded-2xl p-6'>
                  <h3 className='font-heading font-semibold text-base mb-4'>Quick Start</h3>
                  <ol className='space-y-3 text-sm text-muted-foreground list-decimal list-inside'>
                    <li>Go to <button onClick={() => setTab('keys')} className='text-primary hover:underline font-medium'>API Keys</button> tab → Create your first key</li>
                    <li>Use the key in the <code className='text-xs bg-muted px-1.5 py-0.5 rounded'>Authorization: Bearer sk-...</code> header</li>
                    <li>POST to <code className='text-xs bg-muted px-1.5 py-0.5 rounded'>/api/v1/ocr/extract</code> with your document + field list</li>
                    <li>See full reference at <a href='https://dynaparse.com/docs' target='_blank' rel='noopener noreferrer' className='text-primary hover:underline'>dynaparse.com/docs</a></li>
                  </ol>
                </div>
              </div>
            )}

            {/* ── API Keys ── */}
            {tab === 'keys' && (
              <div className='space-y-4'>
                <div className='flex items-center justify-between'>
                  <h2 className='font-heading font-semibold text-lg'>API Keys</h2>
                  <Button size='sm' onClick={handleCreateKey} disabled={creating} className='gap-1.5'>
                    <Plus className='w-4 h-4' />
                    {creating ? 'Creating...' : 'Create Key'}
                  </Button>
                </div>

                {newKey && (
                  <div className='bg-success/10 border border-success/25 rounded-xl p-4'>
                    <p className='text-xs font-semibold text-success mb-2'>New API key — copy it now, it won&apos;t be shown again</p>
                    <div className='flex items-center gap-2 bg-card rounded-lg px-3 py-2 font-mono text-xs text-foreground border border-border'>
                      <span className='flex-1 truncate'>{newKey}</span>
                      <CopyButton text={newKey} />
                    </div>
                    <Button variant='ghost' size='sm' className='mt-2 text-xs text-muted-foreground' onClick={() => setNewKey(null)}>
                      I&apos;ve saved it, dismiss
                    </Button>
                  </div>
                )}

                {apiKeys.length === 0 ? (
                  <div className='text-center py-16 text-muted-foreground'>
                    <Key className='w-8 h-8 mx-auto mb-3 opacity-30' />
                    <p className='text-sm'>No API keys yet. Create one to get started.</p>
                  </div>
                ) : (
                  <div className='space-y-3'>
                    {apiKeys.map((k) => (
                      <div key={k.keyId} className='bg-card border border-border rounded-xl px-4 py-3 flex items-center justify-between gap-4'>
                        <div>
                          <p className='font-mono text-sm font-medium text-foreground'>{k.prefix}...</p>
                          <p className='text-xs text-muted-foreground mt-0.5'>
                            Created {new Date(k.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <Button
                          variant='ghost'
                          size='sm'
                          onClick={() => handleDeleteKey(k.keyId)}
                          className='text-destructive hover:text-destructive hover:bg-destructive/10 gap-1.5'
                        >
                          <Trash2 className='w-3.5 h-3.5' /> Revoke
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── Usage ── */}
            {tab === 'usage' && (
              <div className='space-y-4'>
                <h2 className='font-heading font-semibold text-lg'>Usage History (last 20)</h2>
                {usage.length === 0 ? (
                  <div className='text-center py-16 text-muted-foreground'>
                    <List className='w-8 h-8 mx-auto mb-3 opacity-30' />
                    <p className='text-sm'>No usage yet. Call the API to see your history here.</p>
                  </div>
                ) : (
                  <div className='bg-card border border-border rounded-2xl overflow-hidden'>
                    <table className='w-full text-sm'>
                      <thead className='bg-muted text-muted-foreground text-xs'>
                        <tr>
                          <th className='text-left px-4 py-3'>Type</th>
                          <th className='text-left px-4 py-3'>Pages</th>
                          <th className='text-right px-4 py-3'>Credits used</th>
                          <th className='text-right px-4 py-3'>Date</th>
                        </tr>
                      </thead>
                      <tbody className='divide-y divide-border'>
                        {usage.map((u, i) => (
                          <tr key={i} className='hover:bg-muted/30 transition-colors'>
                            <td className='px-4 py-3'>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                u.type === 'extract' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                              }`}>
                                {u.type}
                              </span>
                            </td>
                            <td className='px-4 py-3 text-muted-foreground'>{u.pages ?? 1}</td>
                            <td className='px-4 py-3 text-right font-medium text-foreground'>{u.credits}</td>
                            <td className='px-4 py-3 text-right text-muted-foreground text-xs'>
                              {u.createdAt ? new Date(u.createdAt).toLocaleString() : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* ── Examples ── */}
            {tab === 'examples' && (
              <div className='space-y-6'>
                <h2 className='font-heading font-semibold text-lg'>Code Examples</h2>

                {apiKeys.length > 0 && (
                  <div className='bg-success/10 border border-success/25 rounded-xl px-4 py-3 text-sm text-success'>
                    The examples below use your first API key prefix. Replace with your full key.
                  </div>
                )}

                <div>
                  <div className='flex items-center justify-between mb-2'>
                    <p className='text-sm font-medium text-foreground'>curl</p>
                    <CopyButton text={curlSnippet(apiKeys[0]?.prefix ? apiKeys[0].prefix + '...' : 'YOUR_API_KEY')} />
                  </div>
                  <pre className='bg-card border border-border rounded-xl p-4 text-xs font-mono overflow-x-auto text-foreground whitespace-pre'>
                    {curlSnippet(apiKeys[0]?.prefix ? apiKeys[0].prefix + '...' : 'YOUR_API_KEY')}
                  </pre>
                </div>

                <div>
                  <div className='flex items-center justify-between mb-2'>
                    <p className='text-sm font-medium text-foreground'>Python</p>
                    <CopyButton text={pythonSnippet} />
                  </div>
                  <pre className='bg-card border border-border rounded-xl p-4 text-xs font-mono overflow-x-auto text-foreground whitespace-pre'>
                    {pythonSnippet}
                  </pre>
                </div>

                <div className='bg-muted rounded-xl p-4 text-sm text-muted-foreground'>
                  For the full API reference, request/response schemas, and error codes, see{' '}
                  <a
                    href='https://dynaparse.com/docs'
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-primary hover:underline'
                  >
                    dynaparse.com/docs ↗
                  </a>
                </div>
              </div>
            )}

            {/* ── Upgrade ── */}
            {tab === 'upgrade' && (
              <UpgradeTab balance={balance} token={token} />
            )}
          </>
        )}
      </div>
    </div>
  );
}
